import axios from 'axios';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5001/api',
});

export const setToken = (token) => {
  if (token) {
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    localStorage.setItem('token', token);
  } else {
    delete api.defaults.headers.common['Authorization'];
    localStorage.removeItem('token');
  }
};

export const getApiHost = () => {
  const base = api.defaults.baseURL || '';
  // remove trailing /api se existir
  return base.replace(/\/api$/, '');
};

export const ensureLogin = async () => {
  const existing = localStorage.getItem('token');
  if (existing) {
    setToken(existing);
    return existing;
  }
  return null;
};

export const login = async ({ email, password }) => {
  const { data } = await api.post('/auth/login', { email, password });
  setToken(data.token);
  return data;
};

export const logout = () => {
  setToken(null);
};

export const getCurrentUser = () => api.get('/auth/me').then(r => r.data);
export const updateMyPreferences = (preferences) => api.put('/auth/me/preferences', { preferences }).then(r => r.data);
export const uploadMyAvatar = (file) => {
  const form = new FormData();
  form.append('avatar', file);
  return api.put('/auth/me/avatar', form, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
};

export const uploadFile = (file) => {
  const form = new FormData();
  form.append('file', file);
  return api.post('/uploads', form).then(r => r.data);
};

// Projects
export const fetchProjects = () => api.get('/projects').then(r => r.data);
export const createProject = (payload) => api.post('/projects', payload).then(r => r.data);
export const updateProject = (id, payload) => api.put(`/projects/${id}`, payload).then(r => r.data);
export const deleteProject = (id) => api.delete(`/projects/${id}`).then(r => r.data);
export const fetchProjectById = (id) => api.get(`/projects/${id}`).then(r => r.data);
export const fetchProjectActivities = (id) => api.get(`/projects/${id}/activities`).then(r => r.data);
export const fetchProjectRisks = (id) => api.get(`/projects/${id}/risks`).then(r => r.data);
export const fetchProjectReports = (id) => api.get(`/projects/${id}/reports`).then(r => r.data);

// Activities
export const fetchActivities = () => api.get('/activities').then(r => r.data);
export const createActivity = (payload) => api.post('/activities', payload).then(r => r.data);
export const updateActivity = (id, payload) => api.put(`/activities/${id}`, payload).then(r => r.data);
export const deleteActivity = (id) => api.delete(`/activities/${id}`).then(r => r.data);

// Deliverables
export const fetchDeliverablesByActivity = (activityId) => api.get(`/activities/${activityId}/deliverables`).then(r => r.data);
export const createDeliverable = (activityId, payload) => api.post(`/activities/${activityId}/deliverables`, payload).then(r => r.data);
export const updateDeliverable = (id, payload) => api.put(`/deliverables/${id}`, payload).then(r => r.data);
export const deleteDeliverable = (id) => api.delete(`/deliverables/${id}`).then(r => r.data);

// Reports
export const fetchReports = () => api.get('/reports').then(r => r.data);
export const createReport = (payload) => api.post('/reports', payload).then(r => r.data);
export const updateReport = (id, payload) => api.put(`/reports/${id}`, payload).then(r => r.data);
export const deleteReport = (id) => api.delete(`/reports/${id}`).then(r => r.data);
export const downloadReport = (id) => api.get(`/reports/${id}/download`, { responseType: 'blob' }).then(r => r.data);
export const shareReportByEmail = (id, emails) => api.post(`/reports/${id}/share`, { emails }).then(r => r.data);

// Evaluations
export const fetchEvaluations = () => api.get('/evaluations').then(r => r.data);
export const fetchEvaluation = (id) => api.get(`/evaluations/${id}`).then(r => r.data);
export const createEvaluation = (payload) => api.post('/evaluations', payload).then(r => r.data);
export const updateEvaluation = (id, payload) => api.put(`/evaluations/${id}`, payload).then(r => r.data);
export const deleteEvaluation = (id) => api.delete(`/evaluations/${id}`).then(r => r.data);
export const sendEvaluationInvites = (id, emails, expiresAt) => api.post(`/evaluations/${id}/send`, { emails, expiresAt }).then(r => r.data);

// Public evaluation (no auth required)
export const getPublicEvaluation = (token) => axios.get(`${getApiHost()}/api/public/evaluations/${token}`).then(r => r.data);
export const submitPublicEvaluation = (token, answers) => axios.post(`${getApiHost()}/api/public/evaluations/${token}/responses`, { answers }).then(r => r.data);
// Public evaluation by generic ID (universal link)
export const getPublicEvaluationById = (id) => axios.get(`${getApiHost()}/api/public/evaluations/by-id/${id}`).then(r => r.data);
export const submitPublicEvaluationById = (id, answers) => axios.post(`${getApiHost()}/api/public/evaluations/by-id/${id}/responses`, { answers }).then(r => r.data);

// Users (admin-only)
export const fetchUsers = () => api.get('/users').then(r => r.data);
export const createUser = (payload) => api.post('/users', payload).then(r => r.data);
export const updateUser = (id, payload) => api.put(`/users/${id}`, payload).then(r => r.data);
export const deleteUser = (id) => api.delete(`/users/${id}`).then(r => r.data);

// Project Team management (admin-only)
export const fetchProjectTeam = (projectId) => api.get(`/projects/${projectId}/team`).then(r => r.data);

export const addProjectMember = (projectId, payload) => api.post(`/projects/${projectId}/team`, payload).then(r => r.data);
export const updateProjectMember = (projectId, userId, payload) => api.put(`/projects/${projectId}/team/${userId}`, payload).then(r => r.data);
export const removeProjectMember = (projectId, userId) => api.delete(`/projects/${projectId}/team/${userId}`).then(r => r.data);

// Schedule Tasks (Cronograma) por Projeto
export const fetchScheduleTasks = (projectId) => api.get(`/projects/${projectId}/tasks`).then(r => r.data);
export const createScheduleTask = (projectId, payload) => api.post(`/projects/${projectId}/tasks`, payload).then(r => r.data);
export const updateScheduleTask = (projectId, taskId, payload) => api.put(`/projects/${projectId}/tasks/${taskId}`, payload).then(r => r.data);
export const deleteScheduleTask = (projectId, taskId) => api.delete(`/projects/${projectId}/tasks/${taskId}`).then(r => r.data);

// Storage (Templates por projeto)
export const listStorage = (projectId, path = '') => api.get(`/storage/${projectId}/list`, { params: { path } }).then(r => r.data);
export const mkdirStorage = (projectId, path, name) => api.post(`/storage/${projectId}/mkdir`, { path, name }).then(r => r.data);
export const uploadStorage = (projectId, path, files) => {
  const form = new FormData();
  (files || []).forEach((f) => form.append('files', f));
  return api.post(`/storage/${projectId}/upload`, form, { params: { path }, headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
};
export const deleteStorage = (projectId, path) => api.delete(`/storage/${projectId}/delete`, { data: { path } }).then(r => r.data);

// Storage Global (Drive)
export const listGlobalStorage = (path = '') => api.get(`/storage/global/list`, { params: { path } }).then(r => r.data);
export const mkdirGlobalStorage = (path, name) => api.post(`/storage/global/mkdir`, { path, name }).then(r => r.data);
export const uploadGlobalStorage = (path, files) => {
  const form = new FormData();
  (files || []).forEach((f) => form.append('files', f));
  return api.post(`/storage/global/upload`, form, { params: { path }, headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
};
export const deleteGlobalStorage = (path) => api.delete(`/storage/global/delete`, { data: { path } }).then(r => r.data);
export const renameGlobalStorage = (path, newName) => api.post(`/storage/global/rename`, { path, newName }).then(r => r.data);
export const moveGlobalStorage = (srcPath, destDirPath) => api.post(`/storage/global/move`, { srcPath, destDirPath }).then(r => r.data);
export const copyGlobalStorage = (srcPath, destDirPath) => api.post(`/storage/global/copy`, { srcPath, destDirPath }).then(r => r.data);
export const createGlobalShareLink = (path, perm = 'view', expiresInMinutes = 1440) => api.post(`/storage/global/share`, { path, perm, expiresInMinutes }).then(r => r.data);

export default api;
